/**
 * Header component — Brand Book §3.1 (v6 mockup)
 * 56px height, bg-card, text-only nav links, standalone theme toggle
 * 3 items: Dashboard, My Deals, Upload
 * User dropdown: click-to-toggle, keyboard accessible
 */

import { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { User, LogOut, Shield, Moon, Sun, Settings, MessageSquare, Edit, Menu } from 'react-feather';
import { useAuthStore } from '../../stores/authStore';
import { useChatStore } from '../../stores/chatStore';
import { useThemeStore } from '../../stores/themeStore';
import { useUIStore } from '../../stores/uiStore';
import { useIdleHide } from '../../hooks/useIdleHide';
import { useMobile } from '../../hooks/useMobile';
import { getChatEnabled } from '../../features/chat/lib/chat-access';
import { Logo } from '../ui';
import { SubscriptionBadge } from '../../features/pricing/components/SubscriptionBadge';

const TRAILING_VARIANT_STYLES = {
  neutral: 'bg-surface-subtle text-txt-secondary border border-border',
  success: 'bg-success/10 text-success border border-success/20',
  warning: 'bg-orange/10 text-orange border border-orange/20',
  error:   'bg-error/10 text-error border border-error/20',
  info:    'bg-info/10 text-info border border-info/20',
} as const;

const navItems = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'My Deals', href: '/deals' },
  { label: 'Upload', href: '/upload' },
];

// Conditional nav items rendered alongside `navItems`. Each entry is gated
// by a per-user predicate evaluated inside the component (see `extraNavItems`
// below) — keeping the static list above for the always-visible items.
interface ExtraNavItem {
  label: string;
  href: string;
  visible: boolean;
}

export function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const userInitial = user?.email?.charAt(0).toUpperCase() || '?';
  const isAdmin = user?.user_role === 'ADMIN';
  // Mirror backend require_chat_enabled: ADMIN/OPS bypass the per-user flag.
  // (The global env switch is enforced server-side; we can't see it here, so
  // an admin who clicks the icon while the global is off will just get a 403
  // toast from the stream hook.)
  const chatEnabled = getChatEnabled(user);
  const toggleChat = useChatStore((s) => s.toggle);
  const chatIsOpen = useChatStore((s) => s.isOpen);
  const resetSession = useChatStore((s) => s.resetSession);
  const openHistoryDrawer = useChatStore((s) => s.openHistoryDrawer);
  const { pathname } = useLocation();
  // The Menu trigger only renders on the chat-only mobile surface.
  // Desktop has the persistent RecentChats sidebar; /settings doesn't
  // need chat access.
  const isMobileChatRoute = pathname === '/chat';
  const breadcrumbs = useUIStore((s) => s.breadcrumbs);
  const trailing = useUIStore((s) => s.breadcrumbTrailing);
  const hasCrumbs = breadcrumbs.length > 0;

  // Auto-hide is mobile-only. On mobile the sticky 56px banner competes
  // with the chat surface, so the header retracts after a few seconds
  // of idle (4s default — tune in useIdleHide) and reveals on any
  // mouse / touch / scroll / keyboard activity (including the 8px
  // top-edge sentinel in MainLayout). On desktop the header is always
  // visible — the idle timer fires but is ignored.
  const isActive = useIdleHide(4000);
  const chatInputFocused = useChatStore((s) => s.chatInputFocused);
  const isMobile = useMobile();
  // On mobile, also force-hide the header the moment the chat textarea
  // takes focus so the sticky banner doesn't block the first sent
  // message.
  const isVisible = !isMobile || (isActive && !chatInputFocused);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) {return;}
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {setMenuOpen(false);}
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  const menuAction = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  return (
    <nav
      className={clsx(
        'bg-surface-card border-b border-border sticky top-0 z-40',
        'transition-transform duration-300 ease-out',
        !isVisible && '-translate-y-full',
      )}
      // Hidden header should not catch clicks behind it. The aria-hidden
      // flag also keeps screen readers from announcing the offscreen
      // content. Reveal-on-hover-near-top is handled by the 8px sentinel
      // strip rendered in MainLayout, which fires the document-level
      // listeners in useIdleHide.
      style={!isVisible ? { pointerEvents: 'none' } : undefined}
      aria-hidden={!isVisible}
    >
      <div className="flex items-center justify-between h-14 px-6">
        {/* Left: Logo + optional breadcrumb chain */}
        <div className="flex items-center gap-0 min-w-0">
          {/* Mobile recent-chats trigger — opens the ChatHistoryDrawer
              rendered by ChatPage. Only on /chat (mobile-only surface);
              desktop already has the persistent sidebar. */}
          {isMobileChatRoute && chatEnabled && (
            <button
              type="button"
              onClick={openHistoryDrawer}
              className={clsx(
                'w-8 h-8 mr-1 md:hidden flex items-center justify-center flex-shrink-0',
                'border border-transparent transition-all duration-150',
                'hover:bg-surface-subtle hover:border-border',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-1',
                'text-txt-tertiary hover:text-txt-secondary'
              )}
              aria-label="Open recent chats"
              title="Recent chats"
            >
              <Menu size={18} />
            </button>
          )}

          <Logo size="md" linkTo={pathname.startsWith('/portal') ? '/portal' : '/dashboard'} />

          {/* Breadcrumb — shown inline after logo when set */}
          {hasCrumbs && (
            <ol
              className="hidden md:flex items-center gap-0 ml-4 min-w-0"
              // 2px nudge down to optically baseline against the logo's
              // capital-letter cap height. Pure visual offset — doesn't
              // affect layout or surrounding spacing.
              style={{ listStyle: 'none', transform: 'translateY(2px)' }}
            >
              {/* Separator between logo and first crumb */}
              <span
                className="mr-2 text-txt-disabled select-none flex-shrink-0"
                style={{ fontSize: '13px' }}
                aria-hidden="true"
              >
                ›
              </span>
              {breadcrumbs.map((item, i) => (
                <li key={i} className="flex items-center gap-0 min-w-0">
                  {i > 0 && (
                    <span
                      className="mx-1.5 text-txt-disabled select-none flex-shrink-0"
                      style={{ fontSize: '13px' }}
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  )}
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="text-txt-secondary hover:text-txt-primary transition-colors duration-150 truncate"
                      style={{ fontSize: '12px' }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className="text-txt-primary font-medium truncate"
                      style={{ fontSize: '12px' }}
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  )}
                </li>
              ))}
              {/* Trailing metadata badges */}
              {trailing.length > 0 && (
                <li className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                  {trailing.map((t, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${TRAILING_VARIANT_STYLES[t.variant]}`}
                    >
                      {t.text}
                    </span>
                  ))}
                </li>
              )}
            </ol>
          )}
        </div>

        {/* Right: Nav links + theme toggle + avatar */}
        <div className="flex items-center gap-5">
          {/* Navigation — text-only, 11px/500, gap-5 */}
          <div className="hidden md:flex items-center gap-5">
            {[
              ...navItems,
              ...((
                [
                  { label: 'Chat', href: '/chat', visible: chatEnabled },
                ] as ExtraNavItem[]
              ).filter((i) => i.visible)),
            ].map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  clsx(
                    'text-body-sm tracking-[0.01em] transition-colors duration-150',
                    isActive
                      ? 'text-txt-primary font-semibold'
                      : 'text-txt-secondary font-medium hover:text-txt-primary'
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Subscription badge */}
          <SubscriptionBadge />

          {/* New chat — mobile only. Mirrors the desktop sidebar's "+ New chat"
              affordance for users locked to the chat-only mobile surface. */}
          {chatEnabled && (
            <button
              onClick={() => {
                resetSession();
                navigate('/chat');
              }}
              className={clsx(
                'w-8 h-8 md:hidden flex items-center justify-center flex-shrink-0',
                'border border-transparent transition-all duration-150',
                'hover:bg-surface-subtle hover:border-border',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-1',
                'text-txt-tertiary hover:text-txt-secondary'
              )}
              aria-label="Start new chat"
              title="Start new chat"
            >
              <Edit size={15} />
            </button>
          )}

          {/* Chat toggle — gated by feature_flags.chat_enabled. Cmd+/ also toggles.
              Hidden on mobile because the user is already on the chat-only /chat
              surface and the right-rail panel doesn't exist there. */}
          {chatEnabled && (
            <button
              onClick={toggleChat}
              className={clsx(
                'w-8 h-8 hidden md:flex items-center justify-center flex-shrink-0',
                'border border-transparent transition-all duration-150',
                'hover:bg-surface-subtle hover:border-border',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-1',
                chatIsOpen ? 'text-orange' : 'text-txt-tertiary hover:text-txt-secondary'
              )}
              aria-label={chatIsOpen ? 'Close AI chat' : 'Open AI chat'}
              aria-pressed={chatIsOpen}
              title="Toggle AI chat (Cmd+/)"
            >
              <MessageSquare size={15} />
            </button>
          )}

          {/* Theme toggle — 32×32, moon/sun */}
          <button
            onClick={toggleTheme}
            className={clsx(
              'w-8 h-8 flex items-center justify-center flex-shrink-0',
              'text-txt-tertiary border border-transparent',
              'hover:text-txt-secondary hover:bg-surface-subtle hover:border-border',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-1',
              'transition-all duration-150'
            )}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {/* User Avatar with Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className={clsx(
                'w-9 h-9 bg-orange text-white font-semibold',
                'flex items-center justify-center',
                'hover:bg-orange-hover transition-colors duration-200',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-2'
              )}
              aria-label="User menu"
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              {userInitial}
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className={clsx(
                  'absolute right-0 mt-1 w-56 py-2 z-50',
                  'bg-surface-card border border-border shadow-lg'
                )}
                style={{ animation: 'dropdownEnter 150ms ease' }}
                role="menu"
              >
                {/* User Info */}
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-body font-semibold text-txt-primary truncate">
                    {user?.email}
                  </p>
                  <p className="text-caption text-txt-tertiary mt-0.5">
                    {user?.company_name}
                  </p>
                  {isAdmin && (
                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-warning-light text-warning text-overline uppercase font-bold">
                      <Shield size={12} />
                      Admin
                    </span>
                  )}
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {isAdmin && (
                    <button
                      role="menuitem"
                      onClick={() => menuAction(() => navigate('/admin'))}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-body text-txt-secondary',
                        'hover:bg-surface-subtle hover:text-txt-primary transition-colors',
                        'focus-visible:bg-surface-subtle focus-visible:text-txt-primary focus-visible:outline-none'
                      )}
                    >
                      <Settings size={16} className="text-txt-tertiary" />
                      Admin panel
                    </button>
                  )}
                  <button
                    role="menuitem"
                    onClick={() => menuAction(() => navigate('/settings'))}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-body text-txt-secondary',
                      'hover:bg-surface-subtle hover:text-txt-primary transition-colors',
                      'focus-visible:bg-surface-subtle focus-visible:text-txt-primary focus-visible:outline-none'
                    )}
                  >
                    <User size={16} className="text-txt-tertiary" />
                    Account settings
                  </button>
                </div>

                {/* Sign Out */}
                <div className="border-t border-border pt-1 mt-1">
                  <button
                    role="menuitem"
                    onClick={() => menuAction(logout)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-body text-error',
                      'hover:bg-error-light transition-colors',
                      'focus-visible:bg-error-light focus-visible:outline-none'
                    )}
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}